import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClients,
  fetchClientById,
  createClient,
  updateClient,
  deleteClient,
  fetchLeads,
  fetchLeadById,
  createLead,
  updateLead,
  deleteLead,
  convertLeadToClient,
  fetchProjects,
  fetchProjectById,
  createProject,
  updateProject,
  deleteProject,
  fetchTasks,
  createTask,
  updateTask,
  completeTask,
  deleteTask,
  createInteraction,
  createQuotation,
  updateQuotation,
  clearErrorState,
  clearSuccessState,
  updateClientFilters,
  updateLeadFilters,
  updateProjectFilters,
  clearSelectedClient,
  clearSelectedLead,
  clearSelectedProject,
} from "../../../app/slice/crmSlice";

const useCRM = () => {
  const dispatch = useDispatch();
  const {
    clients,
    selectedClient,
    clientFilters,
    leads,
    selectedLead,
    leadFilters,
    projects,
    selectedProject,
    projectFilters,
    tasks,
    interactions,
    selectedQuotation,
    loading,
    error,
    success,
  } = useSelector((state) => state.crm);

  // Clients
  const getClients = useCallback((f) => dispatch(fetchClients(f)), [dispatch]);
  const getClientById = useCallback(
    (id) => dispatch(fetchClientById(id)),
    [dispatch],
  );
  const createNewClient = useCallback(
    (data) => dispatch(createClient(data)).unwrap(),
    [dispatch],
  );
  const updateExistingClient = useCallback(
    (id, data) => dispatch(updateClient({ id, data })).unwrap(),
    [dispatch],
  );
  const deleteExistingClient = useCallback(
    (id) => dispatch(deleteClient(id)).unwrap(),
    [dispatch],
  );

  // Leads
  const getLeads = useCallback((f) => dispatch(fetchLeads(f)), [dispatch]);
  const getLeadById = useCallback(
    (id) => dispatch(fetchLeadById(id)),
    [dispatch],
  );
  const createNewLead = useCallback(
    (data) => dispatch(createLead(data)).unwrap(),
    [dispatch],
  );
  const updateExistingLead = useCallback(
    (id, data) => dispatch(updateLead({ id, data })).unwrap(),
    [dispatch],
  );
  const deleteExistingLead = useCallback(
    (id) => dispatch(deleteLead(id)).unwrap(),
    [dispatch],
  );
  const convertExistingLeadToClient = useCallback(
    (leadId, clientId) =>
      dispatch(convertLeadToClient({ leadId, clientId })).unwrap(),
    [dispatch],
  );

  // Projects
  const getProjects = useCallback(
    (f) => dispatch(fetchProjects(f)),
    [dispatch],
  );
  const getProjectById = useCallback(
    (id) => dispatch(fetchProjectById(id)),
    [dispatch],
  );
  const createNewProject = useCallback(
    (data) => dispatch(createProject(data)).unwrap(),
    [dispatch],
  );
  const updateExistingProject = useCallback(
    (id, data) => dispatch(updateProject({ id, data })).unwrap(),
    [dispatch],
  );
  const deleteExistingProject = useCallback(
    (id) => dispatch(deleteProject(id)).unwrap(),
    [dispatch],
  );

  // Tasks
  const getTasks = useCallback(() => dispatch(fetchTasks()), [dispatch]);
  const createNewTask = useCallback(
    (data) => dispatch(createTask(data)).unwrap(),
    [dispatch],
  );
  const updateExistingTask = useCallback(
    (id, data) => dispatch(updateTask({ id, data })).unwrap(),
    [dispatch],
  );
  const completeExistingTask = useCallback(
    (id) => dispatch(completeTask(id)).unwrap(),
    [dispatch],
  );
  const deleteExistingTask = useCallback(
    (id) => dispatch(deleteTask(id)).unwrap(),
    [dispatch],
  );

  // Interactions
  const createNewInteraction = useCallback(
    (data) => dispatch(createInteraction(data)).unwrap(),
    [dispatch],
  );

  // Quotations
  const createNewQuotation = useCallback(
    (data) => dispatch(createQuotation(data)).unwrap(),
    [dispatch],
  );
  const updateExistingQuotation = useCallback(
    (id, data) => dispatch(updateQuotation({ id, data })).unwrap(),
    [dispatch],
  );

  // Filters & State
  const clearErrorStateFn = useCallback(
    () => dispatch(clearErrorState()),
    [dispatch],
  );
  const clearSuccessStateFn = useCallback(
    () => dispatch(clearSuccessState()),
    [dispatch],
  );
  const updateClientFiltersFn = useCallback(
    (f) => dispatch(updateClientFilters(f)),
    [dispatch],
  );
  const updateLeadFiltersFn = useCallback(
    (f) => dispatch(updateLeadFilters(f)),
    [dispatch],
  );
  const updateProjectFiltersFn = useCallback(
    (f) => dispatch(updateProjectFilters(f)),
    [dispatch],
  );
  const clearSelectedClientFn = useCallback(
    () => dispatch(clearSelectedClient()),
    [dispatch],
  );
  const clearSelectedLeadFn = useCallback(
    () => dispatch(clearSelectedLead()),
    [dispatch],
  );
  const clearSelectedProjectFn = useCallback(
    () => dispatch(clearSelectedProject()),
    [dispatch],
  );

  return {
    clients,
    selectedClient,
    clientFilters,
    leads,
    selectedLead,
    leadFilters,
    projects,
    selectedProject,
    projectFilters,
    tasks,
    interactions,
    selectedQuotation,
    loading,
    error,
    success,
    getClients,
    getClientById,
    createNewClient,
    updateExistingClient,
    deleteExistingClient,
    getLeads,
    getLeadById,
    createNewLead,
    updateExistingLead,
    deleteExistingLead,
    convertExistingLeadToClient,
    getProjects,
    getProjectById,
    createNewProject,
    updateExistingProject,
    deleteExistingProject,
    getTasks,
    createNewTask,
    updateExistingTask,
    completeExistingTask,
    deleteExistingTask,
    createNewInteraction,
    createNewQuotation,
    updateExistingQuotation,
    clearErrorState: clearErrorStateFn,
    clearSuccessState: clearSuccessStateFn,
    updateClientFilters: updateClientFiltersFn,
    updateLeadFilters: updateLeadFiltersFn,
    updateProjectFilters: updateProjectFiltersFn,
    clearSelectedClient: clearSelectedClientFn,
    clearSelectedLead: clearSelectedLeadFn,
    clearSelectedProject: clearSelectedProjectFn,
  };
};

export default useCRM;
